/**
 * The exercise's manual per-cycle overrides → the wire's `PostSetOverrideInput` rows.
 *
 * The RULE half of this module is gone. It mapped the wire's single discriminated `progressionRule`
 * document, which the backend replaced with a map on 15 August 2026; the map's seam is
 * `progressionWire.ts` and the draft's vocabulary for it is `progressionRules.ts`. What is left here
 * is the OVERRIDE seam, which never changed shape, and it keeps this file rather than moving because
 * `draftToRequest` is its only caller and the eleven prescribed columns it emits are the same eleven
 * `setToInput` emits two functions away.
 *
 * ── THE CONTRACT THIS HONOURS ────────────────────────────────────────────────────────────────
 * Each of these is a 400 or a silent data loss if it is missed:
 *
 * 1. **Every managed exercise emits a `setOverrides` ARRAY, even an empty one.** The request is the
 *    sole source of overrides; the backend carries nothing across a save. An omitted array is not
 *    "leave them alone", it is "clear them all". → this function always returns an array, and
 *    `draftToRequest` always spreads it onto the exercise.
 *
 * 2. **A LADDER member ships its percent ALONE.** A back-off set, and an AMRAP set carrying a
 *    percent, take their load from the top set's actual result in session, so the backend rejects an
 *    override pinning a weight or an effort target on such a row. An override on such a row with no
 *    percent has nothing left to say and is dropped rather than sent empty. → `derivesLoadInSession`
 *    in `setShape.ts` is the predicate, and it is the same one the backend's validator keys on.
 *
 * 3. **An override targeting a WARM-UP set is dropped.** The backend 400s on one. Matched
 *    POSITIONALLY — `sets[setIndex - 1]`, never by the set's own `setIndex` — because the coordinate
 *    the wire uses is the position, and the two can disagree on a tree mid-edit. An override whose
 *    index falls outside the authored set list addresses nothing and goes the same way.
 *
 * 4. **`prescribedTempo` is capped at 16 CHARACTERS.** `SetMeasurementValues` declares
 *    `[MaxLength(16)]` and the column is `varchar(16)`. The CONSTANT is `planCaps.ts`'s
 *    `TEMPO_MAX_LENGTH` and the editor that authors a tempo enforces it; this mapper deliberately
 *    does NOT truncate, because silently shortening a trainer's notation is data loss dressed as
 *    safety.
 *
 * The READ half applies none of these, on purpose: pre-filtering on load would discard a value the
 * backend is currently storing and show the trainer a cell emptying itself. See `readOverrides`.
 *
 * No React, no DOM, no query hook, no i18next: this is `model/`.
 */

import type { TrainWithMeModulesPlansDtosRequestsPostSetOverrideInput } from '~/shared/api/generated/model';

import type { ExerciseDraft } from './planDraft.types';
import { countsAsWork } from './setCounting';
import { derivesLoadInSession } from './setShape';

type SetOverrideInput = TrainWithMeModulesPlansDtosRequestsPostSetOverrideInput;

/**
 * The exercise's manual overrides, materialised for the wire — **always an array**, empty included.
 *
 * The request is the only source of overrides (document-model ADR §2: an omitted cell is CLEARED),
 * so whatever this returns IS the exercise's override set after the save. Returning an array rather
 * than `null` keeps that reading explicit at the call site.
 *
 * Three narrowings, in the order they apply, each stated in the header's contract:
 *
 * 1. An override whose `setIndex` falls outside the authored set list addresses nothing — dropped.
 * 2. An override on a WARM-UP row is dropped: the backend rejects one outright. Matched
 *    POSITIONALLY (`sets[setIndex - 1]`), never by the set's own `setIndex` field, because the
 *    coordinate the wire uses is the position and the two can disagree on a tree mid-edit.
 * 3. A LADDER member ships its percent ALONE, and is dropped entirely when it has no percent to
 *    ship — an override with every field nulled says nothing the base does not already say.
 *
 * ── EVERY prescribed column is emitted, and shipping four of them was a WIPE ─────────────────
 * This mapper emitted weight, percent, RPE and RIR alone until 15 August 2026, on the reasoning
 * that "an override pins a value on the axis the exercise progresses on". Live QA against the real
 * backend disproved it three times over: `TemplateSetMaterializer.OverlayOverride` writes only the
 * columns the request SUPPLIES, so a pin of `{weight: 110, reps: 4}` came back as weight 110 /
 * reps 5 — the base value, still flagged overridden. Mobile authors pins through a generic
 * measurement-values map, so every phone-authored pin on reps, duration, distance, rounds, speed or
 * tempo was destroyed by the next save from this app.
 *
 * All eleven now travel, verbatim, on the same "emit what was read" rule the base sets follow.
 *
 * **Mobile's measurement-contract gate is deliberately NOT ported**, and the reason is local rather
 * than a disagreement: mobile gates its override emitter on `exercise.measurementFields` because a
 * value on an unmeasured column refuses the whole save under exercise-measurements R9. This app's
 * `setToInput` emits every prescribed column of every BASE set ungated — so in the one scenario the
 * gate protects against (a movement's contract narrowing under a stored plan) the base rows refuse
 * the save first, and gating the overrides alone would buy nothing while silently dropping a pin
 * the backend is currently storing. One ungated emitter beside one gated one is two answers to one
 * question; this file gives the same answer as the file next to it.
 */
export function overrideWireInputs(exercise: ExerciseDraft): SetOverrideInput[] {
  return exercise.progressionOverrides
    .filter((override) => override.setIndex >= 1 && override.setIndex <= exercise.sets.length)
    .flatMap((override) => {
      // Positional, matching the coordinate the wire addresses cells by. The bound above makes this
      // index safe; `noUncheckedIndexedAccess` still types it as possibly-absent, and the guard is
      // the honest answer rather than a non-null assertion.
      const target = exercise.sets[override.setIndex - 1];
      if (target === undefined) return [];
      if (!countsAsWork(target.setType)) return [];
      if (derivesLoadInSession(target)) {
        // The percent ALONE, as rule 2 states and as mobile emits. The remaining columns are nulled
        // explicitly rather than omitted: the overlay treats the two identically, and spelling them
        // out is what makes this branch's narrowing readable beside the wide one below.
        return override.prescribedPercent == null
          ? []
          : [
              {
                cycleIndex: override.cycleIndex,
                setIndex: override.setIndex,
                prescribedWeight: null,
                prescribedPercent: override.prescribedPercent,
                prescribedRpe: null,
                prescribedRir: null,
                prescribedReps: null,
                prescribedRestTime: null,
                prescribedDurationSeconds: null,
                prescribedDistanceMeters: null,
                prescribedRounds: null,
                prescribedSpeed: null,
                prescribedTempo: null,
              },
            ];
      }
      return [
        {
          cycleIndex: override.cycleIndex,
          setIndex: override.setIndex,
          prescribedWeight: override.prescribedWeight,
          prescribedPercent: override.prescribedPercent,
          prescribedRpe: override.prescribedRpe,
          prescribedRir: override.prescribedRir,
          // `?? null` rather than the value: these seven are optional on the draft, and an absent
          // one means "the base applies" — which is what the overlay reads a null as.
          prescribedReps: override.prescribedReps ?? null,
          prescribedRestTime: override.prescribedRestTime ?? null,
          prescribedDurationSeconds: override.prescribedDurationSeconds ?? null,
          prescribedDistanceMeters: override.prescribedDistanceMeters ?? null,
          prescribedRounds: override.prescribedRounds ?? null,
          prescribedSpeed: override.prescribedSpeed ?? null,
          prescribedTempo: override.prescribedTempo ?? null,
        },
      ];
    });
}
